import { z, type ZodError } from 'zod'
import { isApiErrorCode, type ApiErrorCode } from '../constants/api-error-codes.js'
import { parseJsonText } from '../helpers/json.js'

/** One problem inside the request: what's wrong, and where (`fields[2].page`). */
export const apiErrorDetailSchema = z.object({
  code: z.string(),
  path: z.string(),
  message: z.string(),
})

export type ApiErrorDetail = z.infer<typeof apiErrorDetailSchema>

/**
 * Zod's flattened error, attached to a validation response so a caller can
 * point at fields without parsing `errors` again. `fieldErrors` values are
 * the messages for that top-level key.
 */
export const validationDetailsSchema = z.object({
  formErrors: z.array(z.string()),
  fieldErrors: z.record(z.string(), z.array(z.string())),
})

export type ValidationDetails = z.infer<typeof validationDetailsSchema>

/**
 * Every error response. `code` is a plain string (not the catalog enum) so a
 * client keeps working when the API adds codes. `details` is only present on
 * a validation failure.
 */
export const apiErrorBodySchema = z.object({
  statusCode: z.number().int(),
  error: z.string().optional(),
  message: z.string(),
  code: z.string(),
  errors: z.array(apiErrorDetailSchema).optional(),
  details: validationDetailsSchema.optional(),
  requestId: z.string(),
})

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>

/**
 * A `message` as a client may receive it. The API sends one string; Nest's
 * default pipe and some proxies send a list. Callers always see one string.
 */
const apiErrorMessageSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((value) => (Array.isArray(value) ? value.join('; ') : value))
  .optional()

/**
 * What a client should accept. A proxy, an old deploy, or a body that isn't
 * the published contract can omit `code` and `requestId`. {@link apiErrorBodySchema}
 * stays the shape the API publishes; this is the shape clients parse.
 */
export const apiErrorResponseSchema = z.object({
  statusCode: z.number().int().optional(),
  error: z.string().optional(),
  message: apiErrorMessageSchema,
  code: z.string().optional(),
  errors: z.array(apiErrorDetailSchema).optional(),
  details: validationDetailsSchema.optional(),
  requestId: z.string().optional(),
})

export type ApiErrorResponse = z.output<typeof apiErrorResponseSchema>

/**
 * The error body inside `text`, or null when it isn't JSON of this shape
 * (an empty body, a proxy HTML page, a JSON array).
 */
export function parseApiErrorResponse(text: string): ApiErrorResponse | null {
  let json: ReturnType<typeof parseJsonText>
  try {
    json = parseJsonText(text)
  } catch {
    return null
  }
  if (json === null) return null
  const parsed = apiErrorResponseSchema.safeParse(json)
  return parsed.success ? parsed.data : null
}

type ZodIssue = ZodError['issues'][number]

/** A custom check's `params.code` when it's in the catalog, else Zod's own code. */
function zodIssueCode(issue: ZodIssue): ApiErrorCode {
  if (issue.code === 'custom') {
    const own: string | number | boolean | object | null | undefined = issue.params?.['code']
    if (typeof own === 'string' && isApiErrorCode(own)) return own
  }
  const code = `validation.${issue.code}`
  return isApiErrorCode(code) ? code : 'validation.custom'
}

/**
 * The `message`, `code`, and `errors` of a 400 for a failed parse. The API's
 * validation pipe and the published client both build the body from this, so
 * a request rejected locally has the same shape as one the API rejects.
 */
export function validationFailure(error: ZodError): {
  message: string
  code: 'validation_failed'
  errors: ApiErrorDetail[]
} {
  const summary = error.issues
    .map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : ''
      return `${path}${issue.message}`
    })
    .join('; ')
  return {
    message: summary || 'Request failed validation',
    code: 'validation_failed',
    errors: error.issues.map((issue) => ({
      code: zodIssueCode(issue),
      path: jsonPath(issue.path),
      message: issue.message,
    })),
  }
}

/** `fields[3].page` from a Zod-style path. */
export function jsonPath(path: readonly PropertyKey[]): string {
  return path
    .map((part, index) => (typeof part === 'number' ? `[${String(part)}]` : index === 0 ? String(part) : `.${String(part)}`))
    .join('')
}
